import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { FileText, Plus, Search, MousePointerClick, Mail, Calendar } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const AdminReportsPage = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [activeTab, setActiveTab] = useState("seo");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // SEO Form - Based on template
  const [seoForm, setSeoForm] = useState({
    period_start: "",
    period_end: "",
    // Performance (Search Console)
    clicks: 0,
    impressions: 0,
    avg_ctr: 0,
    avg_position: 0,
    // Link Building
    total_backlinks_acquired: 0,
    avg_domain_authority: 0,
    new_referring_domains: 0,
    brand_mentions: 0,
    links_removed_lost: 0,
    disavow_links_added: 0,
    // Performance por Dispositivo
    desktop_percentage: 0,
    mobile_percentage: 0,
    tablet_percentage: 0,
    notes: ""
  });

  // Google Ads Form - Based on template
  const [adsForm, setAdsForm] = useState({
    period_start: "",
    period_end: "",
    total_spend: 0,
    impressions: 0,
    clicks: 0,
    ctr: 0,
    avg_cpc: 0,
    conversions: 0,
    cost_per_conversion: 0,
    revenue: 0,
    notes: ""
  });

  // Email Marketing Form - Based on template
  const [emailForm, setEmailForm] = useState({
    period_start: "",
    period_end: "",
    platform: "Brevo",
    emails_sent: 0,
    delivered: 0,
    open_rate: 0,
    click_rate: 0,
    unsubscribe_rate: 0,
    conversions: 0,
    revenue: 0,
    notes: ""
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeoSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClient) {
      toast.error("Selecione um cliente");
      return;
    }
    try {
      await axios.post(`${API_URL}/reports/seo`, {
        ...seoForm,
        client_id: selectedClient
      });
      toast.success("Relatório SEO criado com sucesso");
      setDialogOpen(false);
      resetForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar relatório");
    }
  };

  const handleAdsSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClient) {
      toast.error("Selecione um cliente");
      return;
    }
    try {
      await axios.post(`${API_URL}/reports/ads`, {
        ...adsForm,
        client_id: selectedClient
      });
      toast.success("Relatório Google Ads criado com sucesso");
      setDialogOpen(false);
      resetForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar relatório");
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClient) {
      toast.error("Selecione um cliente");
      return;
    }
    try {
      await axios.post(`${API_URL}/reports/email`, {
        ...emailForm,
        client_id: selectedClient
      });
      toast.success("Relatório Email Marketing criado com sucesso");
      setDialogOpen(false);
      resetForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar relatório");
    }
  };

  const resetForms = () => {
    setSeoForm({
      period_start: "", period_end: "", total_backlinks_acquired: 0,
      avg_domain_authority: 0, new_referring_domains: 0, brand_mentions: 0,
      links_removed_lost: 0, disavow_links_added: 0, notes: ""
    });
    setAdsForm({
      period_start: "", period_end: "", total_spend: 0, impressions: 0,
      clicks: 0, ctr: 0, avg_cpc: 0, conversions: 0, cost_per_conversion: 0,
      revenue: 0, notes: ""
    });
    setEmailForm({
      period_start: "", period_end: "", platform: "Brevo", emails_sent: 0,
      delivered: 0, open_rate: 0, click_rate: 0, unsubscribe_rate: 0,
      conversions: 0, revenue: 0, notes: ""
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-48" />
        <Card><CardContent className="pt-6"><div className="skeleton h-64" /></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
            <FileText className="w-8 h-8 text-blue-600" />
            Gestão de Relatórios
          </h1>
          <p className="text-slate-500 mt-1">
            Inserir dados de relatórios para clientes
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-report-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Relatório
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>
                Novo Relatório
              </DialogTitle>
            </DialogHeader>
            
            <div className="mt-4 space-y-4">
              {/* Client Selection */}
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger data-testid="select-client">
                    <SelectValue placeholder="Selecione um cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.company} - {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Report Type Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="seo" className="flex items-center gap-2" data-testid="tab-seo">
                    <Search className="w-4 h-4" />
                    SEO
                  </TabsTrigger>
                  <TabsTrigger value="ads" className="flex items-center gap-2" data-testid="tab-ads">
                    <MousePointerClick className="w-4 h-4" />
                    Google Ads
                  </TabsTrigger>
                  <TabsTrigger value="email" className="flex items-center gap-2" data-testid="tab-email">
                    <Mail className="w-4 h-4" />
                    Email
                  </TabsTrigger>
                </TabsList>

                {/* SEO Form */}
                <TabsContent value="seo">
                  <form onSubmit={handleSeoSubmit} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Data Início *</Label>
                        <Input
                          type="date"
                          value={seoForm.period_start}
                          onChange={(e) => setSeoForm({ ...seoForm, period_start: e.target.value })}
                          required
                          data-testid="seo-period-start"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Data Fim *</Label>
                        <Input
                          type="date"
                          value={seoForm.period_end}
                          onChange={(e) => setSeoForm({ ...seoForm, period_end: e.target.value })}
                          required
                          data-testid="seo-period-end"
                        />
                      </div>
                    </div>

                    {/* Performance (Search Console) */}
                    <div className="pt-4 border-t">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">Performance (Search Console)</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Cliques</Label>
                          <Input
                            type="number"
                            value={seoForm.clicks}
                            onChange={(e) => setSeoForm({ ...seoForm, clicks: parseInt(e.target.value) || 0 })}
                            data-testid="seo-clicks"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Impressões</Label>
                          <Input
                            type="number"
                            value={seoForm.impressions}
                            onChange={(e) => setSeoForm({ ...seoForm, impressions: parseInt(e.target.value) || 0 })}
                            data-testid="seo-impressions"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="space-y-2">
                          <Label>CTR Médio (%)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={seoForm.avg_ctr}
                            onChange={(e) => setSeoForm({ ...seoForm, avg_ctr: parseFloat(e.target.value) || 0 })}
                            data-testid="seo-ctr"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Posição Média</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={seoForm.avg_position}
                            onChange={(e) => setSeoForm({ ...seoForm, avg_position: parseFloat(e.target.value) || 0 })}
                            data-testid="seo-position"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Performance por Dispositivo */}
                    <div className="pt-4 border-t">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">Performance por Dispositivo (%)</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Desktop</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={seoForm.desktop_percentage}
                            onChange={(e) => setSeoForm({ ...seoForm, desktop_percentage: parseFloat(e.target.value) || 0 })}
                            data-testid="seo-desktop"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Mobile</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={seoForm.mobile_percentage}
                            onChange={(e) => setSeoForm({ ...seoForm, mobile_percentage: parseFloat(e.target.value) || 0 })}
                            data-testid="seo-mobile"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Tablet</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={seoForm.tablet_percentage}
                            onChange={(e) => setSeoForm({ ...seoForm, tablet_percentage: parseFloat(e.target.value) || 0 })}
                            data-testid="seo-tablet"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Link Building */}
                    <div className="pt-4 border-t">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">Link Building</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Total Backlinks Adquiridos</Label>
                          <Input
                            type="number"
                            value={seoForm.total_backlinks_acquired}
                            onChange={(e) => setSeoForm({ ...seoForm, total_backlinks_acquired: parseInt(e.target.value) || 0 })}
                            data-testid="seo-backlinks"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>DA Médio (Domain Authority)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={seoForm.avg_domain_authority}
                            onChange={(e) => setSeoForm({ ...seoForm, avg_domain_authority: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="space-y-2">
                          <Label>Novos Referring Domains</Label>
                          <Input
                            type="number"
                            value={seoForm.new_referring_domains}
                            onChange={(e) => setSeoForm({ ...seoForm, new_referring_domains: parseInt(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Menções da Marca</Label>
                          <Input
                            type="number"
                            value={seoForm.brand_mentions}
                            onChange={(e) => setSeoForm({ ...seoForm, brand_mentions: parseInt(e.target.value) || 0 })}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="space-y-2">
                          <Label>Links Removidos/Perdidos</Label>
                          <Input
                            type="number"
                            value={seoForm.links_removed_lost}
                            onChange={(e) => setSeoForm({ ...seoForm, links_removed_lost: parseInt(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Links Disavow Adicionados</Label>
                          <Input
                            type="number"
                            value={seoForm.disavow_links_added}
                            onChange={(e) => setSeoForm({ ...seoForm, disavow_links_added: parseInt(e.target.value) || 0 })}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Key Insights / Notas</Label>
                      <Textarea
                        value={seoForm.notes}
                        onChange={(e) => setSeoForm({ ...seoForm, notes: e.target.value })}
                        placeholder="Principais insights e observações do período..."
                      />
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-seo">
                      Guardar Relatório SEO
                    </Button>
                  </form>
                </TabsContent>

                {/* Google Ads Form */}
                <TabsContent value="ads">
                  <form onSubmit={handleAdsSubmit} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Data Início *</Label>
                        <Input
                          type="date"
                          value={adsForm.period_start}
                          onChange={(e) => setAdsForm({ ...adsForm, period_start: e.target.value })}
                          required
                          data-testid="ads-period-start"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Data Fim *</Label>
                        <Input
                          type="date"
                          value={adsForm.period_end}
                          onChange={(e) => setAdsForm({ ...adsForm, period_end: e.target.value })}
                          required
                          data-testid="ads-period-end"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Total Spend (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={adsForm.total_spend}
                          onChange={(e) => setAdsForm({ ...adsForm, total_spend: parseFloat(e.target.value) || 0 })}
                          data-testid="ads-spend"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Revenue (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={adsForm.revenue}
                          onChange={(e) => setAdsForm({ ...adsForm, revenue: parseFloat(e.target.value) || 0 })}
                          data-testid="ads-revenue"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Impressões</Label>
                        <Input
                          type="number"
                          value={adsForm.impressions}
                          onChange={(e) => setAdsForm({ ...adsForm, impressions: parseInt(e.target.value) || 0 })}
                          data-testid="ads-impressions"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Cliques</Label>
                        <Input
                          type="number"
                          value={adsForm.clicks}
                          onChange={(e) => setAdsForm({ ...adsForm, clicks: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>CTR (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={adsForm.ctr}
                          onChange={(e) => setAdsForm({ ...adsForm, ctr: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>CPC Médio (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={adsForm.avg_cpc}
                          onChange={(e) => setAdsForm({ ...adsForm, avg_cpc: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Conversões</Label>
                        <Input
                          type="number"
                          value={adsForm.conversions}
                          onChange={(e) => setAdsForm({ ...adsForm, conversions: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Custo/Conversão (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={adsForm.cost_per_conversion}
                          onChange={(e) => setAdsForm({ ...adsForm, cost_per_conversion: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-700">
                        <strong>ROAS (calculado):</strong> {adsForm.total_spend > 0 ? (adsForm.revenue / adsForm.total_spend).toFixed(2) : '0.00'}x
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Key Insights / Notas</Label>
                      <Textarea
                        value={adsForm.notes}
                        onChange={(e) => setAdsForm({ ...adsForm, notes: e.target.value })}
                        placeholder="Principais insights e observações do período..."
                      />
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-ads">
                      Guardar Relatório Google Ads
                    </Button>
                  </form>
                </TabsContent>

                {/* Email Marketing Form */}
                <TabsContent value="email">
                  <form onSubmit={handleEmailSubmit} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Data Início *</Label>
                        <Input
                          type="date"
                          value={emailForm.period_start}
                          onChange={(e) => setEmailForm({ ...emailForm, period_start: e.target.value })}
                          required
                          data-testid="email-period-start"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Data Fim *</Label>
                        <Input
                          type="date"
                          value={emailForm.period_end}
                          onChange={(e) => setEmailForm({ ...emailForm, period_end: e.target.value })}
                          required
                          data-testid="email-period-end"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Plataforma</Label>
                      <Select value={emailForm.platform} onValueChange={(v) => setEmailForm({ ...emailForm, platform: v })}>
                        <SelectTrigger data-testid="email-platform">
                          <SelectValue placeholder="Selecione a plataforma" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Brevo">Brevo</SelectItem>
                          <SelectItem value="Mailchimp">Mailchimp</SelectItem>
                          <SelectItem value="ActiveCampaign">ActiveCampaign</SelectItem>
                          <SelectItem value="Klaviyo">Klaviyo</SelectItem>
                          <SelectItem value="SendGrid">SendGrid</SelectItem>
                          <SelectItem value="Outro">Outro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Emails Enviados</Label>
                        <Input
                          type="number"
                          value={emailForm.emails_sent}
                          onChange={(e) => setEmailForm({ ...emailForm, emails_sent: parseInt(e.target.value) || 0 })}
                          data-testid="email-sent"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Emails Entregues</Label>
                        <Input
                          type="number"
                          value={emailForm.delivered}
                          onChange={(e) => setEmailForm({ ...emailForm, delivered: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Open Rate (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={emailForm.open_rate}
                          onChange={(e) => setEmailForm({ ...emailForm, open_rate: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Click Rate (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={emailForm.click_rate}
                          onChange={(e) => setEmailForm({ ...emailForm, click_rate: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Unsubscribe Rate (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={emailForm.unsubscribe_rate}
                          onChange={(e) => setEmailForm({ ...emailForm, unsubscribe_rate: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Conversões</Label>
                        <Input
                          type="number"
                          value={emailForm.conversions}
                          onChange={(e) => setEmailForm({ ...emailForm, conversions: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Revenue (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={emailForm.revenue}
                          onChange={(e) => setEmailForm({ ...emailForm, revenue: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Key Insights / Notas</Label>
                      <Textarea
                        value={emailForm.notes}
                        onChange={(e) => setEmailForm({ ...emailForm, notes: e.target.value })}
                        placeholder="Principais insights e observações do período..."
                      />
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-email">
                      Guardar Relatório Email Marketing
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Instructions Card */}
      <Card className="stat-card bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Como inserir relatórios
              </h3>
              <p className="text-slate-600 mt-1">
                1. Clique em "Novo Relatório" <br />
                2. Selecione o cliente <br />
                3. Escolha o tipo de relatório (SEO, Google Ads ou Email Marketing) <br />
                4. Preencha os dados do período e guarde
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Search className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Relatórios SEO</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  Criar Novo
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-50 rounded-lg">
                <MousePointerClick className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Relatórios Ads</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  Criar Novo
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-50 rounded-lg">
                <Mail className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Relatórios Email</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  Criar Novo
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminReportsPage;
